import React, { useContext, useEffect, useState } from "react";
import { isConstructorDeclaration } from "typescript";
import { ApiAdapter } from "../utils/adapters";
import { useDB } from "../utils/custom_hooks/UseDB";
import { ModalContainer } from "./modalContainer";
import {
  PatientGoalSetHandler,
  SetupHandler,
  TemplateHandler,
} from "../utils/state_handlers";
import { PatientGoalDashboard } from "./patientGoalDashboard";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import { pageListeners } from "../utils/listeners";
import { DataCourier } from "./dataCourier";
import { TMDataContext } from "../utils/custom_hooks/TMDataContext";
import { PatientGoal } from "../goal_templates/classes/patient_goal_class";
import {
  ErrorMessage,
  GoalFormNavigationButton,
} from "../goal_templates/common/components/generalUtilities";
import { useStatus } from "../utils/custom_hooks/UseStatus";

const PatientIndex = ({ goals_configs, profileId: patientProfileId }) => {
  const [DBObject, setDBObject] = useDB({
    configVariables: goals_configs,
    PatientId: patientProfileId,
  });
  const [pageStatus, setPageStatus] = useStatus({
    loading: true,
    loadFail: false,
    isCustom: false,
    noTemplates: false,
    viewOnly: true,
  });
  const [modalStatus, setModalStatus] = useState("closed");
  const [selectedPatientGoals, setSelectedPatientGoals] = useState<any>(
    undefined
  );
  const [currentInterviewList, setCurrentInterviewList] = useState<any>([]);

  const [t, i18n] = useTranslation();
  const [selectedTemplate, setSelectedTemplate] = useState();

  var newApi = new ApiAdapter();
  const [api] = useState(newApi);
  pageListeners.detectPatientInterviews(setCurrentInterviewList);

  const [monitorProgramId, setMonitorProgramId] = useState(undefined);
  const [goalTemplateSet, setGoalTemplateSet] = useState<any>([]);
  const [errorFound, setErrorFound] = useState<number[]>([]);


  useEffect(() => {
    api
      .loadSetup()
      .then((payload: any) => {
        return new SetupHandler(payload, setDBObject);
      })
      .then(() => {
        if (!patientProfileId) return [];
        api
          .loadEnrolmentData(patientProfileId) // load exitsting templates
          .then((payload) => {
            if (payload.goalTemplates.length == 0) {
              setPageStatus("noTemplates", true);
            } else {
              setPageStatus("noTemplates", false);
            }
            let set = new TemplateHandler(payload.goalTemplates);
            setGoalTemplateSet(set);

            return payload;
          })
          .then(
            (payload) =>
              new PatientGoalSetHandler(
                payload.patientGoals,
                setSelectedPatientGoals
              )
          );
      })
      .finally(() => {
        pageListeners.detectPatientInterviewsOnLoad(setCurrentInterviewList);
      })
      .catch(() => {
        setPageStatus("loadFail", true);
      });
  }, []);

  useEffect(() => {
    if (!!selectedPatientGoals) setPageStatus("loading", false);
  }, [selectedPatientGoals]);

  useEffect(() => {
    $(function () {
      $("#load_new_plan")[0]
        ? setPageStatus("viewOnly", false)
        : setPageStatus("viewOnly", true);
    });
  }, []);

  // SET EVENT LISTENER FOR MONITOR PROGRAM CHANGE
  useEffect(() => {
    pageListeners.monitorProgramIsSelected(
      setMonitorProgramId,
      monitorProgramId
    );
    pageListeners.customProgramIsSelected(
      t,
      setPageStatus,
      setMonitorProgramId
    );
  }, [monitorProgramId]);

  // LOAD GOAL TEMPLATES FROM NEWLY SELECTED MONITOR PROGRAM
  useEffect(() => {
    if (monitorProgramId == null) return;
    setPageStatus("isCustom", false);
    if (
      typeof parseInt(monitorProgramId) == "number" &&
      monitorProgramId >= 0
    ) {
      api
        .loadGoalTemplates(monitorProgramId)
        .then((goalTemplates) => {
          if (goalTemplates.length == 0) {
            setPageStatus("noTemplates", true);
          } else {
            setPageStatus("noTemplates", false);
          }
          return new TemplateHandler(goalTemplates);
        })
        .then((templates) => {
          setGoalTemplateSet(templates);
          PatientGoal.clearGoals();
          setSelectedPatientGoals([]);
        })
        .finally(() => {
          setPageStatus("loading", false);
        });
    }
  }, [monitorProgramId]);

  const renderComponent = () => {
    if (pageStatus.isCustom)
      return (
        <ErrorMessage
          message={t("views.goals.errors.custom_monitor_program_selected")}
        />
      );
    if (pageStatus.noTemplates)
      return (
        <div>
          {t("views.goals.errors.no_templates_present_on_monitor_program")}
        </div>
      );
    if (!pageStatus.loading)
      return errorFound.length != 0 ? (
        <>
          <ErrorMessage
            message={t("views.goals.errors.patient_editor_interview_mismatch")}
          />
          <PatientGoalDashboard
            profileInterviews={currentInterviewList}
            setErrorFound={setErrorFound}
            setSelectedTemplate={setSelectedTemplate}
          />
        </>
      ) : (
        <PatientGoalDashboard
          profileInterviews={currentInterviewList}
          setErrorFound={setErrorFound}
          setSelectedTemplate={setSelectedTemplate}
        />
      );
    return <div>{t("progress_messages.loading")}</div>;
  };

  return (
    <TMDataContext.Provider
      value={{
        DBObject: DBObject,
        goalTemplateSet: goalTemplateSet,
        setGoalTemplateSet: setGoalTemplateSet,
        selectedPatientGoals: selectedPatientGoals,
        setSelectedPatientGoals: setSelectedPatientGoals,
        setMonitorProgramId: setMonitorProgramId,
        t: t,
        i18n: i18n,
        api: api,
        modalStatus: modalStatus,
        setModalStatus: setModalStatus,
        uuid: uuidv4,
        viewOnly: pageStatus.viewOnly,
      }}
    >
      <DataCourier
        pageLoaded={!pageStatus.loading}
        isCustom={pageStatus.isCustom}
      />
      {pageStatus.viewOnly ? null : (
        <GoalFormNavigationButton
          title="reload_templates"
          key={uuidv4()}
          allow={true}
          handleClick={() => {
            setModalStatus("reload");
          }}
        />
      )}
      {renderComponent()}
      <ModalContainer selection={selectedTemplate} />
    </TMDataContext.Provider>
  );
};

export default PatientIndex;
